import { useId, useRef, type ClipboardEvent } from 'react';
import { cx } from './util.js';

export interface OtpFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  length?: number;
  error?: string | undefined;
  disabled?: boolean;
  /** Called when the last digit is entered. */
  onComplete?: (value: string) => void;
  autoFocus?: boolean;
  /** The label stays for screen readers but is not drawn (the screen's heading says it). */
  labelHidden?: boolean;
}

/**
 * One native input with the one-time-code hint, drawn as cells. Because it is a single input,
 * paste, autofill from SMS and screen readers all behave like an ordinary field.
 */
export function OtpField({
  label,
  value,
  onChange,
  length = 6,
  error,
  disabled = false,
  onComplete,
  autoFocus = false,
  labelHidden = false,
}: OtpFieldProps) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const set = (raw: string) => {
    const next = raw.replace(/\D/g, '').slice(0, length);
    onChange(next);
    if (next.length === length) onComplete?.(next);
  };
  return (
    <div
      className={cx('k-otp', error !== undefined && 'k-otp--error', disabled && 'k-otp--disabled')}
    >
      <label htmlFor={id} className={cx('k-field__label k-t-label-sm', labelHidden && 'k-sr-only')}>
        {label}
      </label>
      <div className="k-otp__cells" onClick={() => input.current?.focus()}>
        {Array.from({ length }, (_, i) => (
          <span
            key={i}
            aria-hidden="true"
            className={cx(
              'k-otp__cell k-t-money',
              value[i] !== undefined && 'k-otp__cell--filled',
              value.length === i && 'k-otp__cell--active',
            )}
          >
            {value[i] ?? ''}
          </span>
        ))}
        <input
          ref={input}
          id={id}
          className="k-otp__input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={length}
          value={value}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={error === undefined ? undefined : `${id}-error`}
          onChange={(event) => set(event.target.value)}
          onPaste={(event: ClipboardEvent<HTMLInputElement>) => {
            event.preventDefault();
            set(event.clipboardData.getData('text'));
          }}
        />
      </div>
      {error === undefined ? null : (
        <p id={`${id}-error`} role="alert" className="k-field__note k-t-label-sm">
          {error}
        </p>
      )}
    </div>
  );
}
